<template>
  <div>
    <workflow-migration-dialog
      v-if="showWorkflowMigrationDialog"
      :migration-campaign="migrationCampaign"
    />
    <manage-labels-dialog
      v-if="showDialog"
      :labels="labels"
      :foreign-id="campaignId"
      endpoint="campaigns"
    />
    <rest-list
      ref="campaigns-table"
      :payload="fetchEndpoint"
      :loading="isLoading"
    >
      <rest-list-column
        v-if="selectedColumns.id"
        prop="id"
        :title="$t('CampaignsList_Field_Id')"
        :min-width="100"
      />
      <rest-list-column-date
        v-if="selectedColumns.deactivate_time"
        prop="deactivate_time"
        :title="$t('CampaignsList_Field_FinishDate')"
        :min-width="200"
      />
      <rest-list-column
        v-if="selectedColumns.title"
        prop="title"
        :title="$t('CampaignsList_Field_Title')"
        :min-width="250"
      >
        <template v-slot:default="scope">
          <div class="campaign-column">
            <router-link :to="rowClickRoute(scope.row)">
              {{ scope.rowValue(scope) }}
            </router-link>
            <div v-if="scope.row.active && scope.row.status">
              <el-tag
                v-if="!workflowsDisabled"
                size="mini"
                effect="plain"
                class="tag"
                :type="scope.row.status === 3 ? 'warning' : 'success'"
              >
                {{ $t(campaignStatusLabels[scope.row.status]) }}
              </el-tag>
              <el-tag
                v-else
                size="mini"
                effect="plain"
                class="tag"
                type="warning"
              >
                {{ $t('CampaignsList_Tag_Disabled') }}
              </el-tag>
            </div>
          </div>
        </template>
      </rest-list-column>
      <rest-list-column-enum
        v-if="selectedColumns.status"
        prop="status_type"
        :enum="campaignStatusEnum"
        :title="$t('CampaignsList_Field_Status')"
        :min-width="140"
        :sortable="false"
        :formatter="formatStatus"
      />
      <rest-list-column-enum
        v-if="selectedColumns.type"
        prop="type"
        :title="$t('CampaignsList_Field_Type')"
        :enum="campaignTypeLabels"
        :min-width="190"
        :sortable="false"
      />
      <rest-list-column-enum
        v-if="selectedColumns.trigger"
        prop="trigger_type"
        :title="$t('CampaignsList_Field_Trigger')"
        :enum="campaignTriggerTypeLabels"
        :min-width="190"
        :sortable="false"
      />
      <rest-list-column-enum
        v-if="selectedColumns.type"
        :enum="MessageTypeLabel"
        prop="message_type"
        :title="$t('CampaignsList_Field_MessageType')"
        :min-width="200"
        :sortable="false"
      />
      <rest-list-column-labels v-if="selectedColumns.labels">
      </rest-list-column-labels>
      <el-table-column
        :label="$t('CampaignsList_Field_SendDate')"
        v-if="selectedColumns.start_date || selectedColumns.end_date"
      >
        <el-table-column
          v-if="selectedColumns.start_date"
          prop="start_time_ts"
          :label="$t('CampaignsList_Field_StartDate')"
          :min-width="100"
        >
          <template slot-scope="{ row }">
            {{ formatDate(row.start_time_ts) }}
          </template>
        </el-table-column>
        <el-table-column
          v-if="selectedColumns.end_date"
          prop="end_time_ts"
          :label="$t('CampaignsList_Field_EndDate')"
          :min-width="100"
        >
          <template slot-scope="{ row }">
            {{ formatDate(row.end_time_ts) }}
          </template>
        </el-table-column>
      </el-table-column>
      <rest-list-column
        v-if="selectedColumns.schedule"
        prop="schedule"
        :title="$t('CampaignsList_Field_Schedule')"
        :min-width="200"
        :filter-input="false"
        :sortable="false"
      />
      <rest-list-column
        v-if="selectedColumns.scheduled_for"
        prop="start_time_ts"
        :title="$t('CampaignsList_Field_ScheduledFor')"
        :min-width="230"
        :filter-input="false"
        :sortable="false"
      >
        <template slot-scope="{ row }">
          {{ formatDate(row.start_time_ts, 'YYYY-MM-DD HH:mm', row.timezone) }}
        </template>
      </rest-list-column>
      <rest-list-column
        v-if="selectedColumns.last_modified"
        prop="last_modified"
        :title="$t('CampaignsList_Field_LastModified')"
        :min-width="140"
        :filter-input="false"
        :sortable="true"
      >
        <template slot-scope="{ row }">
          {{ formatDate(row.last_modified, 'YYYY-MM-DD HH:mm') }}
        </template>
      </rest-list-column>
      <rest-list-column-actions>
        <template slot="header" slot-scope="{}">
          <rest-list-column-settings
            :storage-key="`campaign-list-columns-${status}`"
            :title="$t('CampaignsList_Field_Settings')"
            :columns="columns"
            v-model="selectedColumns"
          />
        </template>
        <template slot-scope="scope">
          <rest-list-action
            v-if="!['draft'].includes(scope.row.status_canonical)"
            :route="actionEndpoint(scope.row, 'log/executions')"
            icon="el-icon-notebook-2"
            :title="$t('CampaignsList_Action_Log')"
          />
          <rest-list-action
            v-if="
              !['scheduled', 'draft'].includes(scope.row.status_canonical) &&
                scope.row.channels.includes('inbox')
            "
            :route="inboxFeedEndpoint(scope.row)"
            icon="el-icon-message"
            :title="$t('CampaignsList_Action_InboxLog')"
          />
          <rest-list-action-start
            v-if="canStartCampaign(scope.row)"
            :endpoint="rowStartEndpoint(scope.row)"
          />
          <rest-list-action-stop
            v-if="canStopCampaign(scope.row)"
            :is-workflow="scope.row.type === campaignTypeEnum.TYPE_WORKFLOW"
            :endpoint="rowStopEndpoint(scope.row)"
          />
          <rest-list-action-close
            v-if="
              scope.row.active &&
                $can('sendPushCampaign') &&
                scope.row.type === campaignTypeEnum.TYPE_WORKFLOW &&
                scope.row.is_open === 1
            "
            :endpoint="rowCancelEndpoint(scope.row)"
          />
          <rest-list-action
            v-if="$can('updateProjectItem') && $can('sendPushCampaign')"
            :route="actionEndpoint(scope.row, 'update')"
            icon="el-icon-edit-outline"
            :title="
              scope.row.active || scope.row.status_canonical === 'draft'
                ? $t('RestList_Action_Update')
                : $t('RestList_Action_ViewCampaign')
            "
          />
          <rest-list-action-view
            v-else
            :route="actionEndpoint(scope.row, 'view')"
          />
          <rest-list-action
            v-if="$can('createProjectItem')"
            :click="() => copyCampaign(scope.row)"
            icon="el-icon-document-copy"
            :title="$t('RestList_Action_Copy')"
          />
          <rest-list-action
            icon="el-icon-price-tag"
            :title="$t('RestList_Action_Label')"
            :click="() => handleLabelsClick(scope.row)"
          />
          <rest-list-action
            v-if="!['scheduled', 'draft'].includes(scope.row.status_canonical)"
            :route="analyticsEndpoint(scope.row)"
            icon="el-icon-data-analysis"
            :title="$t('RestList_Action_Analytics')"
          />
          <rest-list-action
            v-if="
              ['past', 'live'].includes(scope.row.status_canonical) &&
                scope.row.trigger === campaignTriggerTypes.TRIGGER_API &&
                $hasFeature('featureAutomationCampaignExecution')
            "
            :route="taskRoute(scope.row)"
            icon="el-icon-upload2"
            :title="$t('RestList_Action_Import_Triggers')"
          />
          <rest-list-action
            v-if="
              !['scheduled', 'draft'].includes(scope.row.status_canonical) &&
                $can('UserPersonalData::Export')
            "
            :route="exportEndpoint(scope.row)"
            icon="el-icon-download"
            :title="$t('RestList_Action_Download')"
          />
          <rest-list-action-task
            v-if="
              !['scheduled', 'draft'].includes(scope.row.status_canonical) &&
                $can('UserPersonalData::Export')
            "
            icon="el-icon-close"
            :title="$t('RestList_Action_Download_Failed')"
            :endpoint="downloadFailedEndpoint(scope.row)"
          />
          <rest-list-action-delete
            v-if="
              $isRole('admin') ||
                ($can('deleteProjectItem') &&
                  scope.row.status_canonical === 'draft')
            "
            :endpoint="rowDeleteEndpoint(scope.row)"
            :name="scope.row.title"
          />
          <rest-list-action
            v-if="
              $can('createProjectItem') &&
                $can('sendPushCampaign') &&
                $hasFeature('newMxa') &&
                $hasFeature('mxaMigrationTool') &&
                scope.row.type === campaignTypeEnum.TYPE_WORKFLOW &&
                scope.row.new_mxa === 0
            "
            :click="() => handleWorkflowMigrationClick(scope.row)"
            :title="$t('RestList_Action_Workflow_Migrate')"
            icon="el-icon-refresh"
          />
        </template>
      </rest-list-column-actions>
    </rest-list>
  </div>
</template>

<script>
import { MessageTypeLabel } from '@/common/enum/MessageTypeEnum'
import RestListColumn from '@/common/components/rest/RestListColumn'
import { campaignTriggerTypeLabels } from '@/modules/analytics/enum/CampaignTypeLabels'
import { campaignStatusLabels } from '@/modules/analytics/enum/CampaignStatusLabels'
import { campaignSendTypeEnum } from '@/modules/campaigns/enum/CampaignSendTypeEnums'
import {
  campaignTypeEnum,
  campaignTypeLabels
} from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignStatusEnum } from '@/modules/campaigns/enum/CampaignStatusEnum'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import moment from 'moment'
import {
  customConfirm,
  projectEndpoint,
  projectRoute,
  projectTimezoneDate
} from '@/core/helpers'
import RestListActionStart from '@/modules/campaigns/components/RestListActionStart'
import RestListActionStop from '@/modules/campaigns/components/RestListActionStop'
import RestListActionView from '@/common/components/rest/RestListActionView'
import RestListAction from '@/common/components/rest/RestListAction'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListActionTask from '@/common/components/rest/RestListActionTask'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListActionClose from '@/modules/campaigns/components/RestListActionClose'
import RestList from '@/common/components/rest/RestList'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import { EventBus } from '@/core/EventBus'
import ManageLabelsDialog from '@/modules/campaigns/components/ManageLabelsDialog'
import RestListColumnLabels from '@/common/components/rest/RestListColumnLabel'
import WorkflowMigrationDialog from '@/modules/campaigns/components/WorkflowMigrationDialog.vue'
import _ from 'lodash'
import {
  $hasFeature,
  $hasPlatformChannel,
  $hasPlatformActiveAppForChannel
} from '@/core/utils/VueAcl'
import { channelLabels } from '@/modules/campaign-manage/enum/Labels'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'CampaignsList',
  components: {
    RestListColumnLabels,
    ManageLabelsDialog,
    RestListColumnSettings,
    RestList,
    RestListActionTask,
    RestListColumnActions,
    RestListActionClose,
    RestListActionStart,
    RestListColumnDate,
    RestListActionDelete,
    RestListAction,
    RestListActionView,
    RestListActionStop,
    RestListColumnEnum,
    RestListColumn,
    WorkflowMigrationDialog
  },
  props: {
    status: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showDialog: false,
      campaignId: 0,
      labels: [],
      selectedColumns: {},
      MessageTypeLabel,
      campaignStatusLabels,
      campaignTypeLabels,
      campaignTriggerTypeLabels,
      campaignSendTypeEnum,
      campaignTypeEnum,
      campaignStatusEnum,
      campaignTriggerTypes,
      isLoading: false,
      showWorkflowMigrationDialog: false,
      migrationCampaign: null
    }
  },
  mounted() {
    EventBus.$on('manageLabelsDialog', () => {
      this.showDialog = false
    })

    EventBus.$on('applyLabels', () => {
      if (this.$refs['campaigns-table']) {
        this.$refs['campaigns-table'].refresh()
      }
      this.showDialog = false
    })

    EventBus.$on('refreshList', () => {
      this.$refs['campaigns-table'].refresh()
    })

    EventBus.$on('setLoading', isLoading => {
      this.isLoading = isLoading
    })

    EventBus.$on('workflowMigrationDialog', () => {
      this.showWorkflowMigrationDialog = false
    })
  },
  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `campaigns`,
        params: {
          sort: '-last_modified,-id',
          status_type: this.status,
          expand:
            'deactivate_time,schedule,labels,message_type,trigger,trigger_type,send_type,active,status_type,status_canonical,status,type,is_open,start_time_ts,end_time_ts,timezone,last_modified,channels,automatically_pick_winner_schedule_status,new_mxa'
        }
      })
    },
    columns() {
      return {
        id: {
          title: this.$t('CampaignsList_Field_Id'),
          defaultValue: true,
          validation: true,
          qaTag: 'campaign-list-settings-id'
        },
        deactivate_time: {
          title: this.$t('CampaignsList_Field_FinishDate'),
          defaultValue: true,
          validation: this.status === 'past',
          qaTag: 'campaign-list-settings-finish-date'
        },
        title: {
          title: this.$t('CampaignsList_Field_Title'),
          defaultValue: true,
          validation: true,
          qaTag: 'campaign-list-settings-title'
        },
        labels: {
          title: this.$t('CampaignsList_Field_Labels'),
          defaultValue: false,
          validation: true,
          qaTag: 'campaign-list-settings-labels'
        },
        status: {
          title: this.$t('CampaignsList_Field_Status'),
          defaultValue: true,
          validation: this.status === 'all',
          qaTag: 'campaign-list-settings-status'
        },
        trigger: {
          title: this.$t('CampaignsList_Field_Trigger'),
          defaultValue: true,
          validation: true,
          qaTag: 'campaign-list-settings-trigger'
        },
        type: {
          title: this.$t('CampaignsList_Field_MessageType'),
          defaultValue: true,
          validation: true,
          qaTag: 'campaign-list-settings-type'
        },
        start_date: {
          title: this.$t('CampaignsList_Field_StartDate'),
          defaultValue: true,
          validation: this.status === 'all',
          qaTag: 'campaign-list-settings-start-date'
        },
        end_date: {
          title: this.$t('CampaignsList_Field_EndDate'),
          defaultValue: true,
          validation: this.status === 'all',
          qaTag: 'campaign-list-settings-end-date'
        },
        schedule: {
          title: this.$t('CampaignsList_Field_Schedule'),
          defaultValue: true,
          validation: this.status === 'live',
          qaTag: 'campaign-list-settings-schedule'
        },
        scheduled_for: {
          title: this.$t('CampaignsList_Field_ScheduledFor'),
          defaultValue: true,
          validation: this.status === 'scheduled',
          qaTag: 'campaign-list-settings-scheduled-for'
        },
        last_modified: {
          title: this.$t('CampaignsList_Field_LastModified'),
          defaultValue: false,
          validation: true,
          qaTag: 'campaign-list-settings-last-modified'
        }
      }
    },
    workflowsDisabled() {
      return this.$store.state.app.features.maxautomationDisabled
    }
  },
  methods: {
    taskRoute(campaign) {
      return projectRoute({
        name: 'TaskHistory',
        query: {
          filters: JSON.stringify({
            triggered_campaign_id: campaign.id,
            identifier: 'profile_import'
          })
        }
      })
    },
    formatDate(time, format = 'YYYY-MM-DD', hasTimezone = true) {
      if (!time) {
        return '-'
      }
      if (!hasTimezone) {
        return this.$t('CampaignsList_Label_UserTimezone', {
          date: moment
            .unix(time)
            .utc()
            .format(format)
        })
      }
      return projectTimezoneDate(time).format(format)
    },
    formatStatus(status, row) {
      if (
        status === 'Live' &&
        row.type === campaignTypeEnum.TYPE_WORKFLOW &&
        this.workflowsDisabled
      ) {
        return campaignStatusEnum.disabled
      }
      return status
    },
    rowClickRoute(row) {
      if (this.$can('updateProjectItem') && this.$can('sendPushCampaign')) {
        return this.actionEndpoint(row, 'update')
      } else {
        return this.actionEndpoint(row, 'view')
      }
    },
    actionEndpoint(row, action) {
      return `/push/campaign/${action}?id=${row.id}&project_id=${this.$route.query.project_id}`
    },
    exportEndpoint(row) {
      return `/push/export/campaign?id=${row.id}&project_id=${this.$route.query.project_id}`
    },
    analyticsEndpoint(row) {
      return projectRoute({
        name: 'CampaignOverview',
        query: { id: row.id }
      })
    },
    inboxFeedEndpoint(row) {
      return new Promise((resolve, reject) => {
        RestApi({
          method: 'get',
          url: `projects/${this.$route.query.project_id}/actions`,
          params: {
            campaign_id: `${row.id}`
          }
        })
          .then(({ data }) => {
            let tab = this.inboxFeedTabEndpoint(data)
            resolve(
              `/push/inbox/index?campaignId=${row.id}&project_id=${this.$route.query.project_id}${tab}`
            )
          })
          .catch(error => reject(error))
      })
    },
    handleLabelsClick(row) {
      this.labels = row.labels
      this.campaignId = row.id
      this.showDialog = true
    },
    downloadFailedEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/${row.id}/export-failed`
      })
    },
    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `campaigns/${row.id}`
      })
    },
    rowStopEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/${row.id}/stop`,
        params: { id: row.id }
      })
    },
    rowCancelEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/${row.id}/close`,
        params: { id: row.id }
      })
    },
    rowStartEndpoint(row) {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/${row.id}/start`
      })
    },
    copyCampaign(row) {
      const channelsWithErrors = []
      const disabledChannels = []

      Object.keys(channelLabels).forEach(c => {
        if (row.channels.includes(c)) {
          if (!$hasPlatformActiveAppForChannel(c)) {
            channelsWithErrors.push(c)
          }
          if (
            !channelsWithErrors.includes(c) &&
            (!($hasFeature(c) || $hasFeature(_.camelCase(c))) ||
              !$hasPlatformChannel(c))
          ) {
            disabledChannels.push(c)
          }
        }
      })

      if (disabledChannels.length + channelsWithErrors.length > 0) {
        let messages = []

        disabledChannels.forEach(c => {
          messages.push(
            this.$createElement(
              'div',
              { class: 'channel-problem-message' },
              this.$t('ChannelEnablement_ChannelNotAvailable', {
                c: this.$t('ChannelEnablement_Channel_' + c)
              })
            )
          )
        })

        channelsWithErrors.forEach(c => {
          messages.push(
            this.$createElement(
              'div',
              { class: 'channel-problem-message' },
              this.$t('ChannelEnablement_ChannelWithError', {
                c: this.$t('ChannelEnablement_Channel_' + c)
              })
            )
          )
        })

        customConfirm(
          this.$t('ChannelEnablement_ProblemsWithChannels'),
          messages,
          undefined,
          undefined,
          undefined,
          ' channels-alert'
        )
          .then(_ => this.$router.push(this.actionEndpoint(row, 'copy')))
          .catch(_ => {})
      } else {
        this.$router.push(this.actionEndpoint(row, 'copy'))
      }
    },
    canStopCampaign(row) {
      return (
        row.active &&
        this.$can('sendPushCampaign') &&
        (row.send_type !== campaignSendTypeEnum.SEND_TYPE_NOW ||
          row.automatically_pick_winner_schedule_status ===
            'scheduled_sample' ||
          row.type === campaignTypeEnum.TYPE_WORKFLOW)
      )
    },
    canStartCampaign(row) {
      return (
        row.status_canonical !== 'draft' &&
        !row.active &&
        this.$can('sendPushCampaign') &&
        (row.send_type !== campaignSendTypeEnum.SEND_TYPE_NOW ||
          row.automatically_pick_winner_schedule_status ===
            'scheduled_sample') &&
        row.type !== campaignTypeEnum.TYPE_WORKFLOW
      )
    },
    navigateToInboxFeed(row, action) {
      RestApi({
        method: 'get',
        url: `projects/${this.$route.query.project_id}/actions`,
        params: {
          campaign_id: `${row.id}`
        }
      }).then(({ data }) => {
        let tab = data[0] && data[0].expiration_time ? '#tab=expired' : ''
        this.$router.replace(
          `/push/inbox/${action}?campaignId=${row.id}&project_id=${this.$route.query.project_id}${tab}`
        )
      })
    },
    inboxFeedTabEndpoint(data) {
      return data[0] &&
        moment(Date.now() / 1000).isAfter(data[0].expiration_time)
        ? '#tab=expired'
        : '#tab=active'
    },
    handleWorkflowMigrationClick(row) {
      this.migrationCampaign = row
      this.showWorkflowMigrationDialog = true
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-table thead.is-group th {
  background: #fff;
}
.status {
  text-transform: uppercase;
}
.campaign-column {
  display: flex;
  .tag {
    margin-left: 5px;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/CampaignsList.vue